import React, { useState, useEffect } from 'react';
import { useSnackbar, closeSnackbar } from 'notistack';
import {
  Container,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { PlayArrow, CloudUpload, AltRoute, History } from '@mui/icons-material';
import { DSLConfig, EditorState } from '../types/dynamicRouting';
import { useValidateConfigMutation, useSaveConfigMutation, useGetRulesQuery } from '../services/dslApi';
import ConfigEditor from '../components/dynamic-routing/ConfigEditor';
import ValidationPanel from '../components/dynamic-routing/ValidationPanel';
import JsonViewer from '../components/dynamic-routing/JsonViewer';
import ConfigurationViewer from '../components/dynamic-routing/ConfigurationViewer';
import { useEnableDynamicRoutingMutation } from '@/services/assetApi';
import { useGetAssetByIdQuery } from '@/services/assetApi';
import { useParams, Link as RouterLink } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: [1, 3] }}>{children}</Box>}
    </div>
  );
}

const defaultConfig: DSLConfig = {
  version: '1.0.0',
  name: 'New Routing Configuration',
  description: '',
  rules: [
    {
      id: crypto.randomUUID(),
      name: `rule-1`,
      conditions: {},
      gatewayDistribution: [
        {
          gatewayId: '',
          percentage: 100,
        },
      ],
    },
  ],
  gateways: [
    {
      id: '',
      name: '',
    },
  ],
};

function PaymentRulesConfig() {
  const [validateConfig] = useValidateConfigMutation();
  const [saveConfig] = useSaveConfigMutation();
  const [enableDynamicRouting, { isLoading: isMigrating }] = useEnableDynamicRoutingMutation();
  const { assetId } = useParams<{ assetId: string }>();

  const { data: asset, isLoading} = useGetAssetByIdQuery(assetId!);

  // fetch rules from /rules?assetId
const { data: rulesData, isLoading: rulesLoading } = useGetRulesQuery(assetId!, {
  refetchOnMountOrArgChange: true,
  skip: !assetId,
});

  const [state, setState] = useState<EditorState>({
    config: defaultConfig,
    isValidating: false,
    showJsonViewer: false,
  });
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    if (rulesData) {
      setState((prev) => ({
        ...prev,
        config: rulesData,
        validationResult: undefined,
      }));
      setCollapseSignal((prev) => prev + 1);
      enqueueSnackbar("Loaded existing rules", { variant: "info" });
    }
  }, [rulesData]);

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    opts?: { showWarningsButton?: boolean }
  ) => {
    enqueueSnackbar(message, {
      variant: severity,
      autoHideDuration: 6000,
      action: opts?.showWarningsButton
        ? (key) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setTabValue(2); // Navigate to Validation Results tab
              closeSnackbar(key);
            }}
          >
            Show Warnings
          </Button>
        )
        : undefined,
    });
  };

  const handleConfirmMigration = async () => {
    try {
      handleSaveConfig()
      await enableDynamicRouting(assetId!).unwrap();
      setConfirmOpen(false);
      window.location.reload();
    } catch (err) {
      console.error('Migration failed', err);
    }
  };

  const handleConfigChange = (newConfig: DSLConfig) => {
    setState(prev => ({
      ...prev,
      config: newConfig,
      validationResult: undefined,
    }));
  };

  const handleValidate = async (cfg?: DSLConfig) => {
    const configToValidate = cfg || state.config;

    if (!assetId) {
      showSnackbar("Asset ID missing", "error");
      return;
    }

    setState(prev => ({ ...prev, isValidating: true }));

    try {
      const safeConfig = JSON.parse(JSON.stringify(configToValidate));
      const result = await validateConfig(safeConfig).unwrap();
      setState(prev => ({ ...prev, validationResult: result, isValidating: false }));

      if (result.isValid) {
        const warnings = result.summary?.warnings ?? 0;
        if (warnings > 0) {
          showSnackbar(`Configuration is valid with ${warnings} warning(s).`, 'success', { showWarningsButton: true });
        } else {
          showSnackbar('Configuration is valid with no warnings.', 'success');
        }
      } else {
        showSnackbar(`Validation failed: ${result.summary.errors} errors, ${result.summary.warnings} warnings`, 'error');
      }
    } catch (error) {
      setState(prev => ({ ...prev, isValidating: false }));
      showSnackbar('Validation request failed', 'error');
    }
  };

  const handleSaveConfig = async () => {
    if (!assetId) {
      showSnackbar("Asset ID missing", "error");
      return;
    }
    try {
      const safeConfig = JSON.parse(JSON.stringify(state.config));
      await saveConfig({ assetId, config: safeConfig }).unwrap();
      showSnackbar("Configuration deployed successfully", "success");
    } catch (error) {
      showSnackbar("Deploy request failed", "error");
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* <AppBar position="static" sx={{ mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Dynamic Gateway Routing
          </Typography>
          <Button color="inherit" startIcon={<Refresh />} onClick={handleNewConfig} sx={{ mr: 1 }}>
            New
          </Button>
          <Button
            color="inherit"
            startIcon={state.isValidating ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={() => handleValidate()}
            disabled={state.isValidating}
          >
            Validate
          </Button>
        </Toolbar>
      </AppBar> */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<History />}
          component={RouterLink}
          to="history"
        >
          History
        </Button>
        <Button
          variant="contained"
          startIcon={state.isValidating ? <CircularProgress size={20} /> : <PlayArrow />}
          onClick={() => handleValidate()}
          disabled={state.isValidating}
        >
          Validate
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<CloudUpload />}
          onClick={handleSaveConfig}
          disabled={rulesLoading}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          color={asset?.isUsingDynamicGatewayRouting ? "error" : "primary"}
          startIcon={<AltRoute />}
          onClick={() => setConfirmOpen(true)}
          disabled={isMigrating || isLoading}
        >
          {isMigrating
            ? "Updating..."
            : asset?.isUsingDynamicGatewayRouting
              ? "Disable Dynamic Routing"
              : "Enable Dynamic Routing"}
        </Button>
      </Box>

      <Container maxWidth="xl">
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="editor tabs">
              <Tab label="Configuration Viewer" />
              <Tab label="Configuration Editor" />
              <Tab label="Validation Results" />
              <Tab label="Compiled JSON" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <ConfigurationViewer config={state.config} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ConfigEditor
              config={state.config}
              onChange={handleConfigChange}
              validationResult={state.validationResult}
              collapseSignal={collapseSignal}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ValidationPanel
              validationResult={state.validationResult}
              isValidating={state.isValidating}
              onValidate={handleValidate}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <JsonViewer config={state.config} />
          </TabPanel>
        </Paper>
      </Container>

      {/* Migration confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {asset?.isUsingDynamicGatewayRouting
            ? "Disable Dynamic Routing"
            : "Enable Dynamic Routing"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {asset?.isUsingDynamicGatewayRouting
              ? "This will disable dynamic routing for this asset. Do you want to proceed?"
              : "This will migrate the current asset to Dynamic Routing. Do you want to proceed?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmMigration}
            color={asset?.isUsingDynamicGatewayRouting ? "error" : "primary"}
            variant="contained"
            disabled={isMigrating}
          >
            {isMigrating
              ? "Processing..."
              : asset?.isUsingDynamicGatewayRouting
                ? "Disable"
                : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PaymentRulesConfig;
